import React, { useState, useMemo } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Badge } from "@shared/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { useGetActivities } from "@features/mx/resources/hook";
import { useParams } from "react-router-dom";

interface Activity {
  id: string | number;
  currentRead: string | number;
  readingDate: string;
  conditionStatus: string;
  reason: string;
  createdOn: string;
  createdBy: string;
  readFormatValues?: Record<string, string>;
}

export default function ActivitiesTab() {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const { id } = useParams<{ id: string }>();
  const { data: activities, isLoading } = useGetActivities(id || '');

  const tableData = useMemo<Activity[]>(() => {
    const list = activities?.results || activities?.result || [];
    if (!Array.isArray(list)) return [];
    return list.map((item: any) => ({
      id: item.id,
      currentRead: item.currentRead ?? 'NA',
      readingDate: item.readingDate ?? 'NA',
      conditionStatus: item.conditionStatus ?? 'NA',
      reason: item.reason ?? 'NA',
      createdOn: item.createdOn ?? 'NA',
      createdBy: item.createdBy ?? 'NA',
      readFormatValues: item.readFormatValues || {},
    }));
  }, [activities]);

  const columns: TableColumn<Activity>[] = useMemo(
    () => [
      {
        key: "currentRead",
        header: "Current Read",
        type: "text",
        render: (value) => <span className="font-semibold text-gray-900">{value}</span>,
      },
      {
        key: "readingDate",
        header: "Reading Date",
        type: "text",
      },
      {
        key: "conditionStatus",
        header: "Condition Status",
        type: "custom",
        render: (value) => (
          <Badge className="bg-green-50 text-green-700 border border-green-150 shadow-none font-semibold">
            {value}
          </Badge>
        ),
      },
      {
        key: "reason",
        header: "Reason",
        type: "text",
      },
      {
        key: "createdOn",
        header: "Created On",
        type: "text",
      },
      {
        key: "createdBy",
        header: "Created By",
        type: "text",
      },
    ],
    []
  );

  const actions: TableAction[] = [
    {
      label: "View",
      icon: "Eye",
      onClick: (row) => setSelectedActivity(row),
    },
  ];

  const totalItems = activities?.count ?? tableData.length;
  const totalPages = Math.ceil(totalItems / 10) || 1;

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: 10,
      startItem: totalItems === 0 ? 0 : (currentPage - 1) * 10 + 1,
      endItem: Math.min(currentPage * 10, totalItems),
    }),
    [currentPage, totalPages, totalItems]
  );

  return (
    <div className="space-y-4">
      <GenericTable
        data={tableData}
        columns={columns}
        rowKey="id"
        actions={actions}
        actionsType="icons"
        loading={isLoading}
        pagination={pagination}
        onPageChange={(page) => setCurrentPage(page)}
        emptyMessage="No activities found"
      />

      <Dialog open={!!selectedActivity} onOpenChange={(open) => !open && setSelectedActivity(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Read Format Details</DialogTitle>
          </DialogHeader>

          {selectedActivity && selectedActivity.readFormatValues && (
            <div className="grid grid-cols-2 gap-4 mt-2">
              {Object.entries(selectedActivity.readFormatValues).map(([label, val]) => (
                <div key={label} className={label === "Remarks" ? "col-span-2" : ""}>
                  <ValueLabel label={label} value={val || "NA"} />
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
